// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
// Constants for the timeline components.
import { TimelineDataDTO } from '@shared/models/jobrequesttimelines/timeline-data';

export const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const MIN_DAY_CNT = 31;    // Min number of days for rendering.  
export const MAX_DAY_CNT = 365;   // Max number of days for rendering (not currently in use).
export const PAD_DAYS = 10;       // Number of days to pad at the end time.

export const MIN_ZOOM = 20;       // Min zoom level.  Used to set min width of a day cell.
export const MAX_ZOOM = 100;      // Max zoom level.  Used to set max width of a day cell.
export const DEFAULT_ZOOM = 40;

// export const ONE_HOUR_IN_MS = 3600000

// Colors for the ontime status.
export const STANDING_COLORS = {normal: '#99FF99', warning: '#FFCC66', critical: '#FF9933', overdue: '#FF0000', none: '#f8f9f9'};
export const BORDER_COLORS = {normal: '#99FF99', warning: '#FFCC66', critical: '#FF9933', overdue: '#FF0000', none: '#808080'};

export const BORDER_RADIUS = 15;

// Array to simplify checking standings.
export const SET_ED = ['Collected', 'Processed', 'Delivered'];
export const SET_ING = ['Collecting', 'Processing', 'Delivering'];
export const SET_BASE = ['Collect', 'Process', 'Deliver']; 

// Labels for timeline fields.
export const TIMELINE_FIELDS = {
  id: 'id',
  title: 'title',
  elements: 'elements',
};

// Labels for fields of a 'standing' block.
export const BLOCK_FIELDS = {
  id: 'id',
  standing: 'standing',
  start: 'start',
  end: 'end',
  onTimeStatus: 'onTimeStatus',
};

export enum JOB_REQUEST_TYPES {
  none = 0,
  collect = 1,
  process = 2,
  both = 3,
}
export const JOB_REQUEST_TYPE_LABELS = {
  Collect: 'Collect',
  Process: 'Process',
  Both: 'Both'
};

export const BLOCK_FORM_FIELDS = {
  onTimeStatus: 'onTimeStatusId',
  pilot: 'pilotId',
  observer: 'observerId',
  processingTech: 'processingTechId',
  productionTech: 'productionTechId',
  projection: 'projectionId',
  datum: 'datumId',
  ccOutput: 'ccOutputId',
  downloadURL: 'downloadURL',
  asset: 'assetId',
  collection: 'collectionId',
  address: 'fullAddress',
  specialConditions: 'specialConditions',
  priority: 'priority',
  tag1: 'tag1',
  tag2: 'tag2',
  serverName: 'serverName',
  folderPath: 'folderPath',
  serverNameLiDAR: 'serverNameLiDAR',
  folderPathLiDAR: 'folderPathLiDAR',
  serverNameVisuals: 'serverNameVisuals',
  folderPathVisuals: 'folderPathVisuals',
}

export const BLOCK_FORM_BUTTONS = {
  update: 'Update',
  asset: 'Set asset',
  collection: 'Set collection'
}
////

// Text for 'name' of fields in NewLine form.  ie, the query would have form customerId=1&orderID=2&...
export const NEW_LINE_FIELDS = {
  customer: 'customerId',
  order: 'orderId',
  asset: 'assetId',
  lineType: 'typeId',
  vendor: 'vendorId',
  dueDate: 'dueDate',
};

export const CC_FORMATS =[
  {  id: 1,
    label: 'LAS',
  },
  {  id: 2,
    label: '3MX',
  },
  {  id: 3,
    label: 'S3D',
  },
  {  id: 4,
    label: '3SM',
  },
];

export const SAMPLE_OPTS = [
  {  id: 1,
    label: 'placeholder 1',
  },
  {  id: 2,
    label: 'placeholder 2',
  },
  {  id: 3,
    label: 'placeholder 3',
  },
];

// Mocked up JR timeline response for testing.
export const SAMPLE_NEW_TIMELINE = {
  id: 10000,
  title: `New job request-${Date.now()}`,
  elements: [
    {
      id: `10001`,
      standing: 'Collect',
      start: '2021-06-22T00:00:00',
      end: '2021-06-25T23:59:59',
      onTimeStatus: 'normal',
      possibleNextStandings: ['Collect', 'Collecting'],
      selectedPossibleNextStanding: 0,
    },
    {
      id: `10002`,
      standing: 'Process',
      start: '2021-06-26T00:00:00',
      end: '2021-06-27T23:59:59',
      onTimeStatus: 'normal',
      possibleNextStandings: ['Process', 'Processing'],
      selectedPossibleNextStanding: 0,
    },
    {
      id: `10003`,
      standing: 'Deliver',
      start: '2021-06-28T00:00:00',
      end: '2021-06-29T23:59:59',
      onTimeStatus: 'normal',
      possibleNextStandings: ['Deliver', 'Delivering'],
      selectedPossibleNextStanding: 0,
    },
  ]
};


// Sample test data.
export const timelineTestDtos: TimelineDataDTO[] = [
  {
    id: '1',
    title: 'One',
    elements: [
      {
        id: 'VB3_TX-5338_600',
        standing: 'Collected',
        start: '2021-05-20T00:00:00',
        end: '2021-05-25T23:59:59',
        onTimeStatus: 'Normal',
        possibleNextStandings: ['Collected', 'Failed'],
        selectedPossibleNextStanding: 0,              
      },
      {
        id: 'VB3_TX-5338_601',
        standing: 'Process',
        start: '2021-05-26T00:00:00',
        end: '2021-05-28T23:59:59',
        onTimeStatus: 'overdue',
        possibleNextStandings: ['Processing', 'Processed'],
        selectedPossibleNextStanding: 0,              
      },
      {
        id: 'VB3_TX-5338_602',
        standing: 'Deliver',
        start: '2021-05-29T00:00:00',
        end: '2021-05-29T23:59:59',
        onTimeStatus: 'overdue',
        possibleNextStandings: ['Overdue', 'Delivering', 'Delivered'],
        selectedPossibleNextStanding: 1,              
      },
    ],
  },
  {
    id: '7',
    title: 'Two',
    elements: [
      {
        id: 'VB3_TX-5338_600',
        standing: 'Collected',
        start: '2021-05-10T00:00:00',
        end: '2021-05-15T23:59:59',
        onTimeStatus: 'Normal',
        possibleNextStandings: ['Overdue', 'Collected'],
        selectedPossibleNextStanding: 1,              
      },
      {
        id: 'VB3_TX-5338_601',
        standing: 'Process',
        start: '2021-05-16T00:00:00',
        end: '2021-05-16T23:59:59',
        onTimeStatus: 'overdue',
        possibleNextStandings: ['Overdue', 'Processing', 'Processed'],
        selectedPossibleNextStanding: 1,              
      },
      {
        id: 'VB3_TX-5338_602',
        standing: 'Deliver',
        start: '2021-05-17T00:00:00',
        end: '2021-05-18T23:59:59',
        onTimeStatus: 'overdue',
        possibleNextStandings: ['Overdue', 'Delivering', 'Delivered'],
        selectedPossibleNextStanding: 2,              
      },
    ],
  },
  {
    id: '3',
    title: 'Three',
    elements: [
      {
        id: 'VB3_TX-5338_600',
        standing: 'Collected',
        start: '2021-05-07T00:00:00',
        end: '2021-05-12T23:59:59',
        onTimeStatus: 'Normal',
        possibleNextStandings: ['Overdue', 'Failed'],
        selectedPossibleNextStanding: 1,              
      },
      {
        id: 'VB3_TX-5338_601',
        standing: 'Process',
        start: '2021-05-13T00:00:00',
        end: '2021-05-13T23:59:59',
        onTimeStatus: 'overdue',
        possibleNextStandings: ['Processing', 'Processed'],
        selectedPossibleNextStanding: 1,              
      },
      {
        id: 'VB3_TX-5338_602',
        standing: 'Deliver',
        start: '2021-05-14T00:00:00',
        end: '2021-05-15T23:59:59',
        onTimeStatus: 'overdue',
        possibleNextStandings: ['Overdue', 'Delivering', 'Delivered'],
        selectedPossibleNextStanding: 1,              
      },
    ],
  },
];
